import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

import type { ContentfulAsset } from './contentful-api'
import type { LinkEntry, NavigationType } from '@/types/contentful/headerType'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatArticleDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Helper function to get image URL with transformations
export function getImageUrl(
  asset: ContentfulAsset,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'webp' | 'jpg' | 'png'
    fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
  } = {},
): string {
  const { width, height, quality = 80, format = 'webp', fit = 'fill' } = options

  let url = asset?.fields?.file?.url

  // Add query parameters for image transformations
  const params = new URLSearchParams()

  if (width) params.append('w', width.toString())
  if (height) params.append('h', height.toString())
  if (quality) params.append('q', quality.toString())
  if (format) params.append('fm', format)
  if (fit) params.append('fit', fit)

  if (params.toString()) {
    url += `?${params.toString()}`
  }

  return `https:${url}`
}

// Helper function to extract rich text content
export function extractRichTextContent(richTextField: unknown): string {
  if (!richTextField || typeof richTextField !== 'object' || !('content' in richTextField)) {
    return ''
  }

  const content = (richTextField as { content: unknown[] }).content

  if (!Array.isArray(content)) {
    return ''
  }

  return content
    .map((node: unknown) => {
      if (typeof node === 'object' && node && 'nodeType' in node && node.nodeType === 'paragraph') {
        const nodeContent = (node as { content?: unknown[] }).content
        if (Array.isArray(nodeContent)) {
          return nodeContent
            .map((content: unknown) => {
              if (typeof content === 'object' && content && 'value' in content) {
                return String(content.value || '')
              }

              return ''
            })
            .join('')
        }
      }

      return ''
    })
    .join('\n')
    .trim()
}

export function isNavigationEntry(entry: NavigationType | LinkEntry): entry is NavigationType {
  return entry?.sys?.contentType?.sys?.id === 'menuItem' && 'title' in entry.fields && 'layout' in entry.fields && 'links' in entry.fields
}

export function isLinkEntry(entry: NavigationType | LinkEntry): entry is LinkEntry {
  return entry?.sys?.contentType?.sys?.id === 'link' && 'label' in entry.fields && 'url' in entry.fields
}

export function parseJson(value?: string | null) {
  try {
    return JSON.parse(value ?? 'null') ?? null
  } catch {
    return null
  }
}

export function capitalize(s: string) {
  try {
    return String(s[0]).toUpperCase() + String(s).slice(1)
  } catch {
    return s
  }
}

export function slugToTitle(slug: string) {
  if (!slug) return ''

  return slug
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ')
}

export function asHttpSrc(url?: string) {
  if (!url) return null
  if (url.startsWith('https://')) return url
  if (url.startsWith('//')) return `https:${url}`

  return null
}
